import React, { useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { ConfirmationModal, Drawer, DropdownMenu, DropdownMenuItem, IconButton, Spinner, TextButton, Typography, useOverlayContext } from 'seagull';
import { useIoContext, useStore, useStoreActions } from "../../contexts";
import { handleRequest } from "../../infra/utils";
import { useBillingObservable } from "../../providers/BillingObservableProvider";
import { useBillingModeData } from "./BillingDate/useBillingModeData";
import BillingDate from "./BillingDate/BillingDate";
import { getBiCode } from "./BillClientModal.utils";
import { billClientLoader, BillingType, BillMode } from "./BillClientModal.types";
import { createEmptyChargeItem, createEmptyServiceItem, getSubmitChargesData } from "./BillClientItem.utils";
import { ctaTextMap, normalizeEffectiveDate } from "./BillingDate/BillingDate.utils";
import styles from "./BillClientModal.module.scss";
import BillServiceTableItem from "./BillServiceTableItem/BillServiceTableItem";
import BillChargeTableItem from "./BillChargeTableItem/BillChargeTableItem";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import AddNewItemButtons from "./AddNewItemButtons/AddNewItemButtons";
import cn from 'classnames';
import { relationshipModel } from "../../models";
import { TrashLine, WideArrowDownward, WideArrowUpward } from 'seagull/icons';
import { CreditsWarningCallout, RelationshipEffectiveDateCallout } from "./Callouts/Callouts";
import { BIRelationshipScopeProvider } from "../../providers/BIRelationshipScopeProvider";
import { getBillClientServiceOptions, getTimeActivitiesServiceOptions } from "../../business-logic/billServiceSelector";
import { selectors } from "../../store/state-selectors";
import RestfulWrapper from "../RestfulWrapper/RestfulWrapper";
import { TimeActivityCard } from "./TimeActivityCard/TimeActivityCard";
import { TimeActivityActions } from "./TimeActivityActions/TimeActivityActions";
function ScopedBillClientModal(props) {
  const {
    relationship,
    manualServicesBilling,
    invoiceDateInfo,
    totalCredits,
    dismissModal,
    earliestBillableDate,
    defaultItem,
    controlData
  } = props;
  const {
    notifyToRefresh
  } = useBillingObservable();
  const overlayContext = useOverlayContext();
  const {
    api,
    logBI
  } = useStoreActions();
  const {
    getNow
  } = useIoContext();
  const {
    BILL_CLIENT_MULTIPLE_LINES,
    TIME_ACTIVITIES
  } = useFeatureFlags();
  const allowTimeActivities = useStore(selectors.integration.allowTimeActivities);
  const [showTimeActivities, setShowTimeActivities] = useState(false);
  const [isTimeActivitiesServicesSelectorOpen, setIsTimeActivitiesServicesSelectorOpen] = useState(false);
  const [selectedTimeActivitiesService, setSelectedTimeActivitiesService] = useState(null);
  const {
    billModeOptions,
    defaultMode,
    defaultIssueDate
  } = useBillingModeData({
    invoiceDateInfo,
    effectiveDate: relationship.effectiveDate,
    earliestBillableDate
  });
  const form = useForm({
    mode: 'onSubmit',
    defaultValues: {
      items: [defaultItem],
      mode: defaultMode,
      issueDate: defaultIssueDate
    }
  });
  const itemsControl = useFieldArray({
    control: form.control,
    name: 'items'
  });
  const [mode, issueDate, items] = form.watch(['mode', 'issueDate', 'items']);
  const serviceOptions = useMemo(() => getBillClientServiceOptions({
    items: items,
    services: relationship.services,
    controlData
  }), [controlData, relationship.services, items]);
  const timeActivitiesServiceOptions = useMemo(() => {
    if (!allowTimeActivities) {
      return [];
    }
    const options = getTimeActivitiesServiceOptions({
      services: relationship.services,
      controlData
    });
    if (options.length) {
      setSelectedTimeActivitiesService(options[0]);
    }
    return options;
  }, [relationship.services, allowTimeActivities, controlData]);
  async function onSubmit(data) {
    const {
      mode
    } = data;
    overlayContext.startLoader(billClientLoader);
    const {
      success
    } = await handleRequest(sendRequest(data));
    if (!success) {
      overlayContext.showErrorToast('Failed to bill client', "bill-client-".concat(mode, "-error-toast"));
    } else {
      notifyToRefresh();
      dismissModal();
      overlayContext.showSuccessToast('Charges successfully created', "bill-client-".concat(mode, "-success-toast"));
    }
    overlayContext.stopLoader(billClientLoader);
  }
  async function sendRequest(data) {
    const {
      mode,
      issueDate
    } = data;
    const submitChargesData = getSubmitChargesData(data);
    const biCode = getBiCode(mode, issueDate, getNow());
    const apiCall = getApiCall(mode);
    logBI(biCode);
    return apiCall(relationship.id, submitChargesData);
  }
  function getApiCall(mode) {
    return mode === BillMode.Now ? api.relationship.billCharges : api.relationship.submitCharges;
  }
  const handleCloseModal = () => {
    logBI(18404);
    dismissModal();
  };
  const handleDeleteItem = itemIndex => {
    logBI(18405);
    itemsControl.remove(itemIndex);
  };
  const onAddOutOfScopeCharge = () => {
    logBI(18407);
    itemsControl.append(createEmptyChargeItem());
  };
  const onAddServiceFromAgreement = serviceId => {
    logBI(18406, {
      relationshipId: relationship.id
    });
    const service = relationship.services.find(s => s.id === serviceId);
    if (service) {
      itemsControl.append(createEmptyServiceItem(service));
    } else {
      throw new Error("Service with id ".concat(serviceId, " not found for bill client modal add service button"));
    }
  };
  const renderTableItem = (item, itemIndex) => {
    const showDeleteButton = BILL_CLIENT_MULTIPLE_LINES && items.length > 1;
    const deleteButton = testId => /*#__PURE__*/React.createElement(IconButton, {
      onClick: () => handleDeleteItem(itemIndex),
      "data-testid": testId
    }, /*#__PURE__*/React.createElement(TrashLine, null));
    switch (item.type) {
      case BillingType.Service:
        return /*#__PURE__*/React.createElement(BillServiceTableItem, {
          newUI: true,
          key: item.itemId,
          serviceItem: item,
          itemIndex: itemIndex,
          relationshipEffectiveDate: normalizeEffectiveDate(relationship.effectiveDate),
          manualServiceBilling: manualServicesBilling[item.service.id],
          actionColumn: showDeleteButton && deleteButton("bill-service-".concat(itemIndex, "-delete-button"))
        });
      case BillingType.Charge:
        return /*#__PURE__*/React.createElement(BillChargeTableItem, {
          newUI: true,
          key: item.itemId,
          itemIndex: itemIndex,
          chargeItem: item,
          invoiceDateInfo: invoiceDateInfo,
          actionColumn: showDeleteButton && deleteButton("add-charge-".concat(itemIndex, "-delete-button"))
        });
    }
  };
  const handleTimeActivitiesServiceSelected = selectedOption => {
    setSelectedTimeActivitiesService(selectedOption);
    setIsTimeActivitiesServicesSelectorOpen(false);
  };
  return /*#__PURE__*/React.createElement(ConfirmationModal, {
    title: "Bill Client - ".concat(relationship.customer.name),
    width: BILL_CLIENT_MULTIPLE_LINES ? TIME_ACTIVITIES ? '1600px' : '1080px' : '1010px',
    primaryButtonLabel: ctaTextMap[mode],
    "data-testid": "manual-bill",
    textAlignCenter: false,
    onPrimaryButtonClick: form.handleSubmit(onSubmit),
    onCloseButtonClick: handleCloseModal,
    content: /*#__PURE__*/React.createElement(FormProvider, form, /*#__PURE__*/React.createElement("div", {
      className: styles.ensureNoHorizontalScrollFromDrawer
    }, /*#__PURE__*/React.createElement("div", {
      className: cn(BILL_CLIENT_MULTIPLE_LINES && styles.modalContentWrapper)
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.billModalDatesWrapper
    }, /*#__PURE__*/React.createElement(BillingDate, {
      earliestBillableDate: earliestBillableDate,
      billModeOptions: billModeOptions,
      invoiceDateInfo: invoiceDateInfo,
      effectiveDate: relationship.effectiveDate
    }), allowTimeActivities && /*#__PURE__*/React.createElement(TextButton, {
      tooltip: !timeActivitiesServiceOptions.length ? {
        title: 'There are no active unit-based services in the agreement to which time activities can be linked.',
        placement: 'top'
      } : undefined,
      disabled: !timeActivitiesServiceOptions.length,
      weight: "normal",
      className: styles.showTimeActivitiesText,
      "data-testid": "show-time-activities-text",
      onClick: () => setShowTimeActivities(true),
      underline: "none"
    }, "Show time activities")), /*#__PURE__*/React.createElement(CreditsWarningCallout, {
      totalCredits: totalCredits,
      testid: "has-credits-callout"
    }), /*#__PURE__*/React.createElement(RelationshipEffectiveDateCallout, {
      issueDate: issueDate,
      effectiveDate: relationship.effectiveDate,
      testid: "issue-date-before-relationship-effective-date-callout"
    }), items.map(renderTableItem), BILL_CLIENT_MULTIPLE_LINES && /*#__PURE__*/React.createElement(AddNewItemButtons, {
      serviceOptions: serviceOptions,
      handleAddOutOfScopeCharge: onAddOutOfScopeCharge,
      handleAddServiceFromAgreement: onAddServiceFromAgreement
    })), /*#__PURE__*/React.createElement(Drawer, {
      PaperProps: {
        style: {
          position: 'absolute',
          width: 680
        }
      },
      variant: "persistent",
      open: showTimeActivities,
      header: /*#__PURE__*/React.createElement("div", {
        className: styles.timeActivitiesHeader
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "h6",
        weight: "normal"
      }, "Billable Time -"), /*#__PURE__*/React.createElement(DropdownMenu, {
        hAlign: "left",
        maxHeight: 200,
        width: 320,
        onOpenChange: setIsTimeActivitiesServicesSelectorOpen,
        target: /*#__PURE__*/React.createElement("div", {
          className: styles.timeActivitiesServicesSelector
        }, /*#__PURE__*/React.createElement(Typography, {
          variant: "h6",
          ellipsis: true,
          className: styles.timeActivitiesServiceName,
          "data-testid": "time-activities-service-name"
        }, selectedTimeActivitiesService === null || selectedTimeActivitiesService === void 0 ? void 0 : selectedTimeActivitiesService.name), isTimeActivitiesServicesSelectorOpen ? /*#__PURE__*/React.createElement(WideArrowUpward, null) : /*#__PURE__*/React.createElement(WideArrowDownward, {
          className: styles.arrowIcon
        }))
      }, timeActivitiesServiceOptions.map(item => {
        const {
          id,
          name,
          costString
        } = item;
        const isSelected = (selectedTimeActivitiesService === null || selectedTimeActivitiesService === void 0 ? void 0 : selectedTimeActivitiesService.id) === id;
        return /*#__PURE__*/React.createElement(DropdownMenuItem, {
          key: id,
          height: 56,
          skin: "primary",
          "data-testid": "time-activities-service-option-".concat(id),
          onClick: () => handleTimeActivitiesServiceSelected(item),
          selected: isSelected
        }, /*#__PURE__*/React.createElement(Typography, {
          ellipsis: true,
          variant: "paragraph1",
          "data-testid": "option-".concat(id, "-title")
        }, name), /*#__PURE__*/React.createElement(Typography, {
          ellipsis: true,
          variant: "paragraph1",
          skin: "secondary"
        }, costString));
      }))),
      onClose: () => setShowTimeActivities(false),
      onCloseButtonClick: () => setShowTimeActivities(false),
      BackdropProps: {
        invisible: true
      }
    }, /*#__PURE__*/React.createElement(RestfulWrapper, {
      fetch: () => api.qbo.getTimeActivities(relationship.id),
      renderChild: _ref => {
        let {
          data,
          loading,
          refresh
        } = _ref;
        return loading ? /*#__PURE__*/React.createElement(Spinner, {
          centeredInBox: true
        }) : /*#__PURE__*/React.createElement("div", {
          "data-testid": "time-activities-side-panel"
        }, /*#__PURE__*/React.createElement(TimeActivityActions, {
          refresh: refresh
        }), selectedTimeActivitiesService && data.getTimeActivitiesForService(selectedTimeActivitiesService === null || selectedTimeActivitiesService === void 0 ? void 0 : selectedTimeActivitiesService.id).map(timeActivity => /*#__PURE__*/React.createElement(TimeActivityCard, {
          key: timeActivity.id,
          timeActivity: timeActivity
        })));
      }
    }))))
  });
}
export default function BillClientModal(props) {
  const isApproved = relationshipModel.isApproved(props.relationship);
  return /*#__PURE__*/React.createElement(BIRelationshipScopeProvider, {
    relationshipScope: {
      id: props.relationship.id,
      isApproved
    }
  }, /*#__PURE__*/React.createElement(ScopedBillClientModal, props));
}